import {
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import DateFilterField from "./DateFilterField";
import {
  COMPLAINT_CLASSIFICATION_OPTIONS,
  getClassificationLabelKey,
} from "../../utils/complaintClassification";

const MultiSelectFilter = ({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionValue,
  isRTL,
}) => (
  <Autocomplete
    multiple
    options={options}
    value={options.filter((o) => value.includes(getOptionValue(o)))}
    onChange={(_, newValue) => onChange(newValue.map(getOptionValue))}
    getOptionLabel={getOptionLabel}
    isOptionEqualToValue={(opt, val) => getOptionValue(opt) === getOptionValue(val)}
    renderTags={(tagValue, getTagProps) =>
      tagValue.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={getOptionValue(option)}
          label={getOptionLabel(option)}
          size="small"
        />
      ))
    }
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder={label} />
    )}
    sx={{ width: "100%" }}
    dir={isRTL ? "rtl" : "ltr"}
  />
);

const TicketSearchFilters = ({
  filters,
  onChange,
  onSearch,
  onClear,
  isRTL,
  referenceData,
  userOptions,
  showSearchField = true,
  showActionButtons = true,
  canExport = false,
  isExporting = false,
  onExport,
  variant = "page",
}) => {
  const { t } = useTranslation();
  const lang = isRTL ? "name_ar" : "name_en";

  const set = (field) => (value) => onChange({ ...filters, [field]: value });

  const deptOptions = referenceData.departments || [];
  const branchOptions = referenceData.branches || [];
  const commOptions = referenceData.communicationMethods || [];
  const reasonOptions = referenceData.contactReasons || [];

  const userLabel = (u) =>
    u.arabic_name ? `${u.arabic_name} (${u.username})` : u.username;

  const classificationOptions = COMPLAINT_CLASSIFICATION_OPTIONS.map((value) => ({
    value,
    label: t(getClassificationLabelKey(value)),
  }));

  const gridSize = variant === "drawer" ? { xs: 12 } : { xs: 12, sm: 6, md: 3 };

  const filterFields = (
    <Grid container spacing={2}>
      {showSearchField && (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            placeholder={t("search.searchPlaceholder")}
            value={filters.q}
            onChange={(e) => set("q")(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch?.()}
            InputProps={{
              [isRTL ? "endAdornment" : "startAdornment"]: (
                <InputAdornment position={isRTL ? "end" : "start"}>
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}

      <Grid size={gridSize}>
        <FormControl fullWidth>
          <InputLabel>{t("search.filterStatus")}</InputLabel>
          <Select
            value={filters.status}
            label={t("search.filterStatus")}
            onChange={(e) => set("status")(e.target.value)}
          >
            <MenuItem value="all">{t("search.allStatuses")}</MenuItem>
            <MenuItem value="open">{t("search.status.open")}</MenuItem>
            <MenuItem value="closed">{t("search.status.closed")}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterComplaintClassification")}
          options={classificationOptions}
          value={filters.complaintClassification}
          onChange={set("complaintClassification")}
          getOptionLabel={(o) => o.label}
          getOptionValue={(o) => o.value}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterContactReason")}
          options={reasonOptions}
          value={filters.contactReasons}
          onChange={set("contactReasons")}
          getOptionLabel={(o) => o[lang] || o.name_ar}
          getOptionValue={(o) => o.reason_id ?? o.contact_reason_id ?? o.id}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterCommunicationMethod")}
          options={commOptions}
          value={filters.communicationMethods}
          onChange={set("communicationMethods")}
          getOptionLabel={(o) => o[lang] || o.name_ar}
          getOptionValue={(o) => o.method_id ?? o.communication_method_id ?? o.id}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterDepartments")}
          options={deptOptions}
          value={filters.departments}
          onChange={set("departments")}
          getOptionLabel={(o) => o[lang] || o.name_ar}
          getOptionValue={(o) => o.department_id ?? o.id}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterBranches")}
          options={branchOptions}
          value={filters.branches}
          onChange={set("branches")}
          getOptionLabel={(o) => o[lang] || o.name_ar}
          getOptionValue={(o) => o.branch_id ?? o.id}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <DateFilterField
          label={t("search.filterCreatedFrom")}
          value={filters.createdFrom}
          onChange={set("createdFrom")}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <DateFilterField
          label={t("search.filterCreatedTo")}
          value={filters.createdTo}
          onChange={set("createdTo")}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <DateFilterField
          label={t("search.filterUpdatedFrom")}
          value={filters.updatedFrom}
          onChange={set("updatedFrom")}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <DateFilterField
          label={t("search.filterUpdatedTo")}
          value={filters.updatedTo}
          onChange={set("updatedTo")}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterCreators")}
          options={userOptions}
          value={filters.creators}
          onChange={set("creators")}
          getOptionLabel={userLabel}
          getOptionValue={(o) => o.id}
          isRTL={isRTL}
        />
      </Grid>

      <Grid size={gridSize}>
        <MultiSelectFilter
          label={t("search.filterParticipants")}
          options={userOptions}
          value={filters.participants}
          onChange={set("participants")}
          getOptionLabel={userLabel}
          getOptionValue={(o) => o.id}
          isRTL={isRTL}
        />
      </Grid>

      {showActionButtons && (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {canExport && (
              <Button
                variant="outlined"
                onClick={onExport}
                disabled={isExporting}
                startIcon={
                  isExporting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
              >
                {t("search.exportExcel")}
              </Button>
            )}
            <Button variant="outlined" onClick={onClear}>
              {t("search.clearButton")}
            </Button>
            <Button variant="contained" onClick={onSearch} startIcon={<SearchIcon />}>
              {t("search.searchButton")}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  if (variant === "drawer") {
    return filterFields;
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      {filterFields}
    </Paper>
  );
};

export default TicketSearchFilters;
